import { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ItemVisual } from '../components/ItemVisual';
import { audio } from '../core/audio';
import { useActivity } from './useActivity';

// ONE component for every drag game. Two rules decide its whole shape:
//
//   1. TAP MUST ALSO COMPLETE IT. Tap the object, tap the destination, same
//      reward, same stars. A 2-year-old whose press-hold-move-release control
//      has not formed yet must still be able to finish. Drag is an
//      enhancement, never the only path.
//   2. A RELEASE THAT MISSES IS NOT A WRONG ANSWER. It floats home in silence
//      and nothing is counted. Only a release onto a genuinely WRONG target is
//      an attempt, and that uses the same gentle retry every other game uses.
//
// DragBasket.jsx had neither, plus an exact-hit drop test, and the owner's
// child could not play it. That component is the reason this one exists.

// A release within this fraction of the screen width from a target's centre
// counts as a drop on it. Generous on purpose: a 2-year-old aims badly.
const SNAP_RATIO = 0.25;
// Below this travel the gesture was a TAP, not a drag, so the drop test must
// not run — otherwise tapping an item that merely sits near a basket would
// teleport it there.
const TAP_SLOP = 12;

export function DragDrop({ activity, onComplete }) {
  const { state, answer, showHint } = useActivity(activity, onComplete);
  const [placed, setPlaced] = useState({});     // item index -> target id
  const [dragIdx, setDragIdx] = useState(null); // finger is down on this item
  const [selIdx, setSelIdx] = useState(null);   // lifted by tap, waiting for a target
  const [pos, setPos] = useState(null);
  // Optional "look at this, now it is hidden" preamble (bring_back). One flag,
  // not a second component.
  const [peek, setPeek] = useState(!!activity.showFirst);
  const targetRefs = useRef({});
  const startRef = useRef(null);
  const doneRef = useRef(false);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (!activity.showFirst) return undefined;
    const t = setTimeout(() => setPeek(false), activity.showMs || 2200);
    return () => clearTimeout(t);
  }, [activity.id]); // eslint-disable-line

  // The prompt is not always just a voice line. `sound` means "listen to this,
  // then find it" — without playing it the child is told to listen and hears
  // nothing, which is an unplayable round, not a hard one. `names` speaks the
  // items the child is being asked for, so the game stops being a guess.
  const playPrompt = async () => {
    await audio.playVoice(activity.voice, activity.voiceAlt);
    if (activity.sound) await audio.playObjectSound(activity.sound);
    for (const w of activity.names || []) await audio.playVoice(w);
  };

  useEffect(() => {
    if (activity.showFirst) {
      const look = setTimeout(() => audio.playVoice('mem_look'), 350);
      const t = setTimeout(playPrompt, (activity.showMs || 2200) + 200);
      return () => { clearTimeout(look); clearTimeout(t); };
    }
    const t = setTimeout(playPrompt, 400);
    return () => clearTimeout(t);
  }, [activity.id]); // eslint-disable-line

  const accepts = (item, tid) => activity.allCorrect || item.target === tid;

  // Nearest target centre inside the snap radius. Anything actually inside the
  // element counts regardless of radius, so a big screen never gets stricter.
  const nearestTarget = (x, y) => {
    const limit = window.innerWidth * SNAP_RATIO;
    let best = null;
    let bestD = Infinity;
    for (const [tid, el] of Object.entries(targetRefs.current)) {
      if (!el) continue;
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const d = Math.hypot(x - cx, y - cy);
      const inside = x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
      if ((inside || d <= limit) && d < bestD) { bestD = d; best = { id: tid, inside }; }
    }
    return best;
  };

  const place = (idx, tid) => {
    if (state === 'correct' || placed[idx] !== undefined) return;
    const item = activity.items[idx];
    setSelIdx(null);
    if (!accepts(item, tid)) {
      // Wrong destination — a real attempt. answer(false) plays the same gentle
      // retry line every other game uses and pulses a hint after two. No red,
      // no buzz, no shake, Piko unchanged.
      answer(false);
      return;
    }
    audio.playSfx('drop');
    if (activity.sounds && activity.sounds[item.visual.id]) {
      audio.playObjectSound(activity.sounds[item.visual.id]);
    }
    const next = { ...placed, [idx]: tid };
    setPlaced(next);
    // `needed` is how many must land, which is NOT always every item: a
    // "choose the one that…" round shows three and wants one.
    const needed = activity.needed || activity.items.length;
    if (Object.keys(next).length >= needed && !doneRef.current) {
      doneRef.current = true;
      answer(true);
    }
  };

  const down = (idx) => (e) => {
    if (peek || state === 'correct' || placed[idx] !== undefined) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    audio.playSfx('pickup');
    startRef.current = { x: e.clientX, y: e.clientY };
    setDragIdx(idx);
    setSelIdx(idx);
    setPos({ x: e.clientX, y: e.clientY });
  };

  const move = (e) => {
    if (dragIdx === null) return;
    setPos({ x: e.clientX, y: e.clientY });
  };

  const up = (e) => {
    if (dragIdx === null) return;
    const idx = dragIdx;
    const s = startRef.current;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    setDragIdx(null);
    setPos(null);
    const travelled = s ? Math.hypot(e.clientX - s.x, e.clientY - s.y) : 0;
    // A tap: leave the item lifted and wait for the child to tap a destination.
    if (travelled < TAP_SLOP) return;
    const hit = nearestTarget(e.clientX, e.clientY);
    // Missed everything — float home, silently. Not an attempt, nothing counted.
    if (!hit) { setSelIdx(null); return; }
    // The snap radius is deliberately huge (25% of the screen) so a bad aim
    // still SUCCEEDS. It must not also make a bad aim FAIL: on a wide tablet
    // that radius covers half the screen, and a vague release would be scored
    // as choosing the wrong answer. A wrong target only counts when the child
    // actually released on top of it.
    if (!accepts(activity.items[idx], hit.id) && !hit.inside) { setSelIdx(null); return; }
    place(idx, hit.id);
  };

  // The single remaining object counts as already lifted (see the item render),
  // so tapping the destination alone finishes the round.
  const liftedIdx = () => {
    if (selIdx !== null) return selIdx;
    const open = activity.items
      .map((_, i) => i).filter((i) => placed[i] === undefined);
    return open.length === 1 ? open[0] : null;
  };

  const tapTarget = (tid) => () => {
    if (dragIdx !== null) return;
    const idx = liftedIdx();
    if (idx === null) return;
    place(idx, tid);
  };

  // Point at the ITEM to pick up, not at the destination. Eight of these games
  // have a single destination, so pulsing the target told a stuck child
  // nothing — and if they were holding a distractor its target is 'none',
  // which matches no target at all and pulsed nothing.
  const hintIdx = showHint && !activity.allCorrect
    ? activity.items.findIndex((it, i) => placed[i] === undefined
        && activity.targets.some((t) => t.id === it.target))
    : -1;
  const hintTarget = showHint && selIdx !== null && !activity.allCorrect
    ? activity.items[selIdx]?.target : null;

  return (
    <div
      className="relative flex-1 flex flex-col justify-between px-4 pb-6 pt-6 touch-none select-none"
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={() => { setDragIdx(null); setPos(null); }}
      data-testid={`activity-${activity.id}`}
      data-drag-activity="1">

      {/* Softens the painted background so the interactive layer wins. Static —
          it costs no animation budget. */}
      <div className="absolute inset-x-0 bottom-0 h-1/2 pointer-events-none"
        style={{ background: 'linear-gradient(to top, rgba(252,249,242,0.55), rgba(252,249,242,0))' }} />

      {/* "Look at this one" preamble — then it is hidden and the tray appears. */}
      {peek && activity.showItem && (
        <div className="flex-1 flex items-center justify-center" data-testid="drag-peek">
          <div className="rounded-[2rem] p-4 bg-white/80 backdrop-blur"
            style={{ border: '5px solid rgba(255,255,255,0.92)' }}>
            <ItemVisual item={activity.showItem} size={150} />
          </div>
        </div>
      )}

      {/* Items waiting to be moved */}
      {!peek && (
      <div className="flex justify-center items-center gap-4 flex-wrap pt-3 min-h-[120px]">
        {activity.items.map((entry, idx) => {
          if (placed[idx] !== undefined) return null;
          // With one object on screen, lift it for them. Tapping the hole is the
          // most natural first act for a 2-year-old, and nothing on screen ever
          // says "pick it up first".
          const openCount = activity.items.filter((_, i) => placed[i] === undefined).length;
          const lifted = selIdx === idx || (openCount === 1 && selIdx === null);
          const isHint = hintIdx === idx;
          return (
            <motion.div
              key={`${entry.visual.id}-${idx}`}
              data-testid={`drag-item-${entry.visual.id}`}
              data-item-index={idx}
              onPointerDown={down(idx)}
              animate={reduce ? {} : lifted ? { scale: 1.08 } : isHint ? { scale: [1, 1.07, 1] } : { scale: 1 }}
              transition={isHint && !lifted ? { repeat: Infinity, duration: 1.2 } : { duration: 0.18 }}
              className="cursor-grab active:cursor-grabbing rounded-[1.6rem] p-2 bg-white/75 backdrop-blur flex items-center justify-center"
              style={{
                minWidth: 104, minHeight: 104,
                border: '5px solid rgba(255,255,255,0.92)',
                opacity: dragIdx === idx ? 0.25 : 1,
                boxShadow: lifted
                  ? '0 0 0 5px #FFD166, 0 14px 30px rgba(255,209,102,0.45)'
                  : 'inset 0 -6px 0 rgba(0,0,0,0.08), 0 10px 24px rgba(29,53,87,0.15)',
              }}>
              <ItemVisual item={entry.visual} size={88} />
            </motion.div>
          );
        })}
      </div>
      )}

      {/* The travelling ghost — one moving element */}
      {dragIdx !== null && pos && (
        <div className="fixed z-50 pointer-events-none" style={{ left: pos.x - 52, top: pos.y - 52 }}>
          <ItemVisual item={activity.items[dragIdx].visual} size={104} />
        </div>
      )}

      {/* Destinations */}
      {!peek && (
      <div className="flex justify-center items-end gap-5 flex-wrap pb-2">
        {activity.targets.map((tg) => {
          const landed = activity.items
            .map((it, i) => (placed[i] === tg.id ? it : null))
            .filter(Boolean);
          const isHint = hintTarget === tg.id;
          return (
            <motion.button
              key={tg.id}
              ref={(el) => { targetRefs.current[tg.id] = el; }}
              data-testid={`drop-target-${tg.id}`}
              onClick={tapTarget(tg.id)}
              animate={reduce ? {} : isHint ? { scale: [1, 1.06, 1] } : { scale: 1 }}
              transition={isHint ? { repeat: Infinity, duration: 1.2 } : { duration: 0.3 }}
              className="relative rounded-[2rem] p-3 flex items-center justify-center bg-white/70 backdrop-blur-md"
              style={{
                minWidth: 116, minHeight: 116,
                border: '5px solid rgba(255,255,255,0.92)',
                // The painted background depicts this same game, so without a
                // card the real target loses to the artwork behind it — a
                // 2-year-old reaches for the painted hole instead. Same
                // treatment TapChoice uses, for the same reason.
                boxShadow: isHint
                  ? '0 0 0 5px #FFD166, 0 12px 30px rgba(255,209,102,0.5)'
                  : 'inset 0 -6px 0 rgba(0,0,0,0.08), 0 10px 24px rgba(29,53,87,0.18)',
              }}>
              {/* A painted target takes the colour that was dropped on it —
                  that visible change IS the reward. */}
              <ItemVisual
                item={activity.paints && landed.length ? { ...tg.visual, color: tg.colour } : tg.visual}
                size={104} />
              {landed.length > 0 && !activity.paints && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <motion.div
                    initial={reduce ? false : { scale: 0 }}
                    animate={{ scale: 1 }}
                    className="flex items-center justify-center gap-1">
                    {landed.slice(0, 3).map((it, i) => (
                      <ItemVisual key={i} item={it.visual} size={44} />
                    ))}
                  </motion.div>
                </div>
              )}
            </motion.button>
          );
        })}
      </div>
      )}
    </div>
  );
}
